import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const formatWon = (amount) => `₩${Number(amount || 0).toLocaleString('ko-KR')}`;

const EMPTY_MENU_FORM = {
  name: '',
  description: '',
  price: '',
  imageUrl: '',
  categoryId: '',
  isSeniorFriendly: false,
  options: [],
};

const IMAGE_SIZE_WARN_BYTES = 200 * 1024; // 200KB

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconXMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">{t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconXMark /> : <IconInfo />}</span>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

const MenuManagePage = () => {
  const { user } = useAuth();
  const { toasts, addToast } = useToast();
  const storeId = user?.storeId || user?.store_id;

  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Menu modal
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuForm, setMenuForm] = useState(EMPTY_MENU_FORM);
  const [isSavingMenu, setIsSavingMenu] = useState(false);
  const [imageWarn, setImageWarn] = useState('');

  // Category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  // Confirm
  const [confirmDialog, setConfirmDialog] = useState(null);

  const fetchData = useCallback(async () => {
    if (!storeId) {
      setCategories([]);
      setMenus([]);
      setIsLoading(false);
      return;
    }
    try {
      const [catRes, menuRes] = await Promise.allSettled([
        api.get(`/stores/${storeId}/categories`),
        api.get(`/stores/${storeId}/menus`),
      ]);

      let cats = [];
      let items = [];

      if (catRes.status === 'fulfilled') {
        const d = catRes.value.data;
        cats = Array.isArray(d) ? d : d.categories || [];
      }

      if (menuRes.status === 'fulfilled') {
        const d = menuRes.value.data;
        items = Array.isArray(d) ? d : d.menus || [];
      }

      setCategories(cats);
      setMenus(items);
      if (cats.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(cats[0]._id || cats[0].id);
      }
    } catch {
      setCategories([]);
      setMenus([]);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, selectedCategoryId]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const openAddMenu = () => {
    setEditingMenu(null);
    setMenuForm({ ...EMPTY_MENU_FORM, categoryId: selectedCategoryId || '' });
    setImageWarn('');
    setShowMenuModal(true);
  };

  const openEditMenu = (menu) => {
    setEditingMenu(menu);
    setImageWarn('');
    setMenuForm({
      name: menu.name || '',
      description: menu.description || '',
      price: menu.price || '',
      imageUrl: menu.imageUrl || menu.image_url || '',
      categoryId: menu.categoryId || menu.category_id || '',
      isSeniorFriendly: menu.isSeniorFriendly || menu.is_senior_friendly || false,
      options: (menu.options || []).map((opt) => ({
        name: opt.name || '',
        priceDelta: String(opt.priceDelta ?? opt.price_delta ?? 0),
      })),
    });
    setShowMenuModal(true);
  };

  const handleSaveMenu = async () => {
    if (!menuForm.name.trim()) { addToast('메뉴 이름을 입력해주세요.', 'error'); return; }
    if (!menuForm.price || isNaN(Number(menuForm.price))) { addToast('가격을 올바르게 입력해주세요.', 'error'); return; }

    setIsSavingMenu(true);
    const payload = {
      name: menuForm.name.trim(),
      description: menuForm.description.trim(),
      price: Number(menuForm.price),
      image_url: menuForm.imageUrl.trim(),
      category_id: menuForm.categoryId || selectedCategoryId,
      is_senior_friendly: menuForm.isSeniorFriendly,
      options: menuForm.options.map((opt) => ({
        name: opt.name,
        price_delta: parseInt(opt.priceDelta, 10) || 0,
      })),
    };

    try {
      if (editingMenu) {
        const id = editingMenu._id || editingMenu.id;
        if (storeId) {
          const res = await api.put(`/stores/${storeId}/menus/${id}`, payload);
          const updated = res.data?.menu || res.data || { ...editingMenu, ...payload };
          // Merge options from payload (backend PUT doesn't return options)
          setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, ...updated, options: payload.options } : m));
        } else {
          setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, ...payload } : m));
        }
        addToast('메뉴가 수정되었습니다.', 'success');
      } else {
        if (storeId) {
          const res = await api.post(`/stores/${storeId}/menus`, payload);
          const created = res.data?.menu || res.data || { _id: `m-${Date.now()}`, ...payload };
          setMenus((prev) => [...prev, created]);
        } else {
          setMenus((prev) => [...prev, { _id: `m-${Date.now()}`, ...payload }]);
        }
        addToast('메뉴가 추가되었습니다.', 'success');
      }
      setShowMenuModal(false);
    } catch (err) {
      addToast(err.response?.data?.message || '저장에 실패했습니다.', 'error');
    } finally {
      setIsSavingMenu(false);
    }
  };

  const handleDeleteMenu = (menu) => {
    setConfirmDialog({
      title: '메뉴 삭제',
      message: `"${menu.name}"을(를) 삭제하시겠습니까?`,
      danger: true,
      onConfirm: async () => {
        const id = menu._id || menu.id;
        try {
          if (storeId) await api.delete(`/stores/${storeId}/menus/${id}`);
          setMenus((prev) => prev.filter((m) => (m._id || m.id) !== id));
          addToast('메뉴가 삭제되었습니다.', 'success');
        } catch { addToast('삭제에 실패했습니다.', 'error'); }
        setConfirmDialog(null);
      },
    });
  };

  const handleToggleSoldOut = async (menu) => {
    const id = menu._id || menu.id;
    const newVal = !menu.isSoldOut;
    setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, isSoldOut: newVal } : m));
    try {
      if (storeId) {
        await api.put(`/admin/menus/${id}/sold-out`, { is_sold_out: newVal ? 1 : 0 });
      }
      addToast(newVal ? `"${menu.name}" 품절 처리되었습니다.` : `"${menu.name}" 판매 재개되었습니다.`, 'success');
    } catch {
      setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, isSoldOut: !newVal } : m));
      addToast('상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleTogglePopular = async (menu) => {
    const id = menu._id || menu.id;
    const newVal = !(menu.isPopular || menu.is_popular);
    setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, isPopular: newVal, is_popular: newVal } : m));
    try {
      if (storeId) {
        await api.put(`/admin/menus/${id}/popular`, { is_popular: newVal ? 1 : 0 });
      }
      addToast(newVal ? `"${menu.name}" 인기 메뉴로 설정되었습니다.` : `"${menu.name}" 인기 메뉴 해제되었습니다.`, 'success');
    } catch {
      setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, isPopular: !newVal, is_popular: !newVal } : m));
      addToast('상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleToggleNew = async (menu) => {
    const id = menu._id || menu.id;
    const newVal = !(menu.isNew || menu.is_new);
    setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, isNew: newVal, is_new: newVal } : m));
    try {
      if (storeId) {
        await api.put(`/admin/menus/${id}/new`, { is_new: newVal ? 1 : 0 });
      }
      addToast(newVal ? `"${menu.name}" NEW 태그가 추가되었습니다.` : `"${menu.name}" NEW 태그가 해제되었습니다.`, 'success');
    } catch {
      setMenus((prev) => prev.map((m) => (m._id || m.id) === id ? { ...m, isNew: !newVal, is_new: !newVal } : m));
      addToast('상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) { addToast('카테고리 이름을 입력해주세요.', 'error'); return; }
    setIsSavingCategory(true);
    try {
      if (storeId) {
        const res = await api.post(`/stores/${storeId}/categories`, { name: newCategoryName.trim() });
        const cat = res.data?.category || res.data || { _id: `cat-${Date.now()}`, name: newCategoryName.trim() };
        setCategories((prev) => [...prev, cat]);
        addToast('카테고리가 추가되었습니다.', 'success');
      } else {
        setCategories((prev) => [...prev, { _id: `cat-${Date.now()}`, name: newCategoryName.trim() }]);
        addToast('카테고리가 추가되었습니다. (데모)', 'success');
      }
      setNewCategoryName('');
      setShowCategoryModal(false);
    } catch { addToast('카테고리 추가에 실패했습니다.', 'error'); }
    finally { setIsSavingCategory(false); }
  };

  const handleDeleteCategory = (cat) => {
    setConfirmDialog({
      title: '카테고리 삭제',
      message: `"${cat.name}" 카테고리를 삭제하시겠습니까?\n해당 카테고리의 메뉴도 함께 삭제될 수 있습니다.`,
      danger: true,
      onConfirm: async () => {
        const id = cat._id || cat.id;
        try {
          if (storeId) await api.delete(`/stores/${storeId}/categories/${id}`);
          setCategories((prev) => prev.filter((c) => (c._id || c.id) !== id));
          if (selectedCategoryId === id) {
            const remaining = categories.filter((c) => (c._id || c.id) !== id);
            setSelectedCategoryId(remaining.length > 0 ? (remaining[0]._id || remaining[0].id) : null);
          }
          addToast('카테고리가 삭제되었습니다.', 'success');
        } catch { addToast('삭제에 실패했습니다.', 'error'); }
        setConfirmDialog(null);
      },
    });
  };

  const addOption = () => {
    setMenuForm((prev) => ({ ...prev, options: [...prev.options, { name: '', priceDelta: '' }] }));
  };

  const updateOption = (idx, field, value) => {
    setMenuForm((prev) => {
      const opts = [...prev.options];
      opts[idx] = { ...opts[idx], [field]: value };
      return { ...prev, options: opts };
    });
  };

  const removeOption = (idx) => {
    setMenuForm((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };

  const filteredMenus = menus.filter((m) => {
    const catId = m.categoryId || m.category_id;
    return catId === selectedCategoryId;
  });

  if (isLoading) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 28 }}>
          <div className="skeleton-line h-24 w-60" />
          <div className="skeleton-line w-40" style={{ height: 12, marginTop: 8 }} />
        </div>
        <div className="menu-layout">
          <div className="skeleton-card" style={{ height: 300 }}>
            <div className="skeleton-line w-60 h-24" style={{ marginBottom: 16 }} />
            {[0,1,2].map((i) => (
              <div key={i} className="skeleton-line w-80" style={{ marginTop: 12 }} />
            ))}
          </div>
          <div>
            <div className="menu-grid">
              {[0,1,2,3,4,5].map((i) => (
                <div key={i} className="skeleton-card animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                  <div style={{ height: 130, background: 'linear-gradient(90deg, #F2F4F6 25%, #E8EAED 50%, #F2F4F6 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s ease infinite', borderRadius: '16px 16px 0 0' }} />
                  <div style={{ padding: 14 }}>
                    <div className="skeleton-line w-60" />
                    <div className="skeleton-line w-40" />
                    <div className="skeleton-line w-40 h-24" style={{ marginTop: 8 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">메뉴 관리</h1>
          <p className="page-subtitle">메뉴를 추가, 수정, 삭제할 수 있습니다</p>
        </div>
        <button className="btn btn-primary" onClick={openAddMenu}>
          + 메뉴 추가
        </button>
      </div>

      <div className="menu-layout">
        {/* Left: Categories */}
        <div className="category-panel">
          <div className="category-panel-header">
            카테고리
            <button
              className="btn btn-sm btn-primary"
              style={{ float: 'right', padding: '3px 10px' }}
              onClick={() => setShowCategoryModal(true)}
            >
              + 추가
            </button>
          </div>

          {categories.length === 0 ? (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
              카테고리가 없습니다
            </div>
          ) : (
            categories.map((cat) => {
              const catId = cat._id || cat.id;
              const count = menus.filter((m) => (m.categoryId || m.category_id) === catId).length;
              return (
                <div
                  key={catId}
                  className={`category-item ${selectedCategoryId === catId ? 'active' : ''}`}
                  onClick={() => setSelectedCategoryId(catId)}
                >
                  <span>{cat.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', background: 'var(--bg)', padding: '1px 7px', borderRadius: 10 }}>
                      {count}
                    </span>
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '2px 5px', display: 'flex', alignItems: 'center' }}
                      onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat); }}
                      title="삭제"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {categories.length === 0 && (
            <div style={{ padding: '12px 16px' }}>
              <button className="btn btn-outline w-full btn-sm" onClick={() => setShowCategoryModal(true)}>
                + 카테고리 추가
              </button>
            </div>
          )}
        </div>

        {/* Right: Menus */}
        <div>
          {/* Category header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <h3 style={{ fontWeight: 700, fontSize: '1rem' }}>
                {categories.find((c) => (c._id || c.id) === selectedCategoryId)?.name || '전체'}
              </h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {filteredMenus.length}개 메뉴
              </p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={openAddMenu}>
              + 메뉴 추가
            </button>
          </div>

          {filteredMenus.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <h3>메뉴가 없습니다</h3>
                <p>이 카테고리에 메뉴를 추가해보세요.</p>
                <button className="btn btn-primary mt-16" onClick={openAddMenu}>
                  + 메뉴 추가
                </button>
              </div>
            </div>
          ) : (
            <div className="menu-grid">
              {filteredMenus.map((menu, idx) => {
                const id = menu._id || menu.id;
                return (
                  <div key={id} className="menu-card animate-fade-in-up" style={{ animationDelay: `${idx * 50}ms` }}>
                    {/* Image or placeholder */}
                    <div className="menu-card-img">
                      {menu.imageUrl || menu.image_url ? (
                        <img
                          src={menu.imageUrl || menu.image_url}
                          alt={menu.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
                          <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/>
                          <path d="M12 6v6l4 2"/>
                        </svg>
                      )}
                    </div>

                    <div className="menu-card-body">
                      <div className="flex-between" style={{ marginBottom: 4 }}>
                        <div className="menu-card-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {menu.name}
                          {(menu.isPopular || menu.is_popular) && (
                            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: '#F59E0B', borderRadius: 4, padding: '1px 5px', lineHeight: '16px' }}>인기</span>
                          )}
                          {(menu.isNew || menu.is_new) && (
                            <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: 'var(--primary)', borderRadius: 4, padding: '1px 5px', lineHeight: '16px' }}>NEW</span>
                          )}
                        </div>
                        {(menu.isSeniorFriendly || menu.is_senior_friendly) && (
                          <span title="시니어 친화 메뉴" style={{ fontSize: '11px', color: 'var(--text-tertiary)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 4px', fontWeight: 600 }}>시니어</span>
                        )}
                      </div>
                      {menu.description && (
                        <div className="menu-card-desc">{menu.description}</div>
                      )}
                      <div className="menu-card-price" style={{ marginTop: 8 }}>
                        {formatWon(menu.price)}
                      </div>
                      {menu.options && menu.options.length > 0 && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                          옵션 {menu.options.length}개
                        </div>
                      )}
                    </div>

                    {/* Toggle buttons for sold-out, popular, new */}
                    <div style={{ display: 'flex', gap: 4, padding: '8px 14px 0', flexWrap: 'wrap' }}>
                      <button
                        className={`btn btn-sm ${menu.isSoldOut ? 'btn-danger' : 'btn-outline'}`}
                        onClick={() => handleToggleSoldOut(menu)}
                        style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                      >
                        {menu.isSoldOut ? '품절 ON' : '품절'}
                      </button>
                      <button
                        className={`btn btn-sm ${(menu.isPopular || menu.is_popular) ? 'btn-warning' : 'btn-outline'}`}
                        onClick={() => handleTogglePopular(menu)}
                        style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                      >
                        {(menu.isPopular || menu.is_popular) ? '인기 ON' : '인기'}
                      </button>
                      <button
                        className={`btn btn-sm ${(menu.isNew || menu.is_new) ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => handleToggleNew(menu)}
                        style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                      >
                        {(menu.isNew || menu.is_new) ? 'NEW ON' : 'NEW'}
                      </button>
                    </div>

                    <div className="menu-card-actions">
                      <button
                        className={`btn btn-sm ${menu.isSoldOut ? 'btn-accent' : 'btn-danger'}`}
                        onClick={() => handleToggleSoldOut(menu)}
                        style={{ flex: 1 }}
                      >
                        {menu.isSoldOut ? '판매재개' : '품절'}
                      </button>
                      <button className="btn btn-sm btn-outline" onClick={() => openEditMenu(menu)}>
                        수정
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={() => handleDeleteMenu(menu)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                      </button>
                    </div>

                    {/* Sold out overlay */}
                    {menu.isSoldOut && (
                      <div className="soldout-overlay">SOLD OUT</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Menu Modal */}
      {showMenuModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowMenuModal(false); }}>
          <div className="modal modal-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingMenu ? '메뉴 수정' : '메뉴 추가'}</h3>
              <button className="modal-close" onClick={() => setShowMenuModal(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">메뉴 이름 *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="메뉴 이름"
                    value={menuForm.name}
                    onChange={(e) => setMenuForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">가격 (원) *</label>
                  <div className="input-with-icon">
                    <span className="input-icon" style={{ left: 10, fontSize: 13 }}>₩</span>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0"
                      value={menuForm.price}
                      onChange={(e) => setMenuForm((p) => ({ ...p, price: e.target.value }))}
                      min={0}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">설명</label>
                <textarea
                  className="form-textarea"
                  placeholder="메뉴 설명을 입력하세요"
                  value={menuForm.description}
                  onChange={(e) => setMenuForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
              </div>

              {/* Image upload section */}
              <div className="form-group">
                <label className="form-label">
                  이미지 <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>(URL 권장 — 빠른 로딩)</span>
                </label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="https://... (이미지 URL 직접 입력)"
                  value={menuForm.imageUrl.startsWith('data:') ? '' : menuForm.imageUrl}
                  onChange={(e) => {
                    setImageWarn('');
                    setMenuForm((p) => ({ ...p, imageUrl: e.target.value }));
                  }}
                  style={{ marginBottom: 8 }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>또는 파일 업로드</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="menu-image-file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setImageWarn('');
                    if (file.size > IMAGE_SIZE_WARN_BYTES) {
                      setImageWarn(`파일 크기가 ${Math.round(file.size / 1024)}KB입니다. 200KB 초과 시 로딩이 느려질 수 있습니다. URL 입력을 권장합니다.`);
                    }
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      setMenuForm((p) => ({ ...p, imageUrl: ev.target.result }));
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }}
                />
                <label
                  htmlFor="menu-image-file"
                  className="btn btn-outline btn-sm"
                  style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  파일 선택
                </label>
                {imageWarn && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, fontSize: '0.8rem', color: '#92400E', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    {imageWarn}
                  </div>
                )}
                {menuForm.imageUrl && (
                  <div style={{ marginTop: 10, position: 'relative', display: 'inline-block' }}>
                    <img
                      src={menuForm.imageUrl}
                      alt="미리보기"
                      style={{ height: 100, maxWidth: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', display: 'block' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => { setMenuForm((p) => ({ ...p, imageUrl: '' })); setImageWarn(''); }}
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', padding: 0 }}
                      title="이미지 제거"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">카테고리</label>
                <select
                  className="form-select"
                  value={menuForm.categoryId || selectedCategoryId || ''}
                  onChange={(e) => setMenuForm((p) => ({ ...p, categoryId: e.target.value }))}
                >
                  <option value="">카테고리 선택</option>
                  {categories.map((cat) => (
                    <option key={cat._id || cat.id} value={cat._id || cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Senior friendly checkbox */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={menuForm.isSeniorFriendly}
                    onChange={(e) => setMenuForm((p) => ({ ...p, isSeniorFriendly: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    시니어 친화 메뉴
                  </span>
                </label>
              </div>

              {/* Options */}
              <div>
                <div className="flex-between" style={{ marginBottom: 10 }}>
                  <label className="form-label" style={{ margin: 0 }}>옵션</label>
                  <button className="btn btn-sm btn-outline" onClick={addOption}>
                    + 옵션 추가
                  </button>
                </div>

                {menuForm.options.length === 0 ? (
                  <div style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem', padding: '8px 0' }}>
                    옵션이 없습니다. "옵션 추가" 버튼을 눌러 추가하세요.
                  </div>
                ) : (
                  menuForm.options.map((opt, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="옵션 이름 (예: 곱빼기)"
                        value={opt.name}
                        onChange={(e) => updateOption(idx, 'name', e.target.value)}
                        style={{ flex: 2 }}
                      />
                      <input
                        type="number"
                        className="form-input"
                        placeholder="추가 가격"
                        value={opt.priceDelta}
                        onChange={(e) => updateOption(idx, 'priceDelta', e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => removeOption(idx)}
                        style={{ color: 'var(--danger)', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowMenuModal(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleSaveMenu} disabled={isSavingMenu}>
                {isSavingMenu ? <><span className="spinner spinner-sm" /> 저장 중...</> : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCategoryModal(false); }}>
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">카테고리 추가</h3>
              <button className="modal-close" onClick={() => setShowCategoryModal(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">카테고리 이름</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="예: 한식, 음료, 디저트"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowCategoryModal(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleAddCategory} disabled={isSavingCategory}>
                {isSavingCategory ? <><span className="spinner spinner-sm" /> 추가 중...</> : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDialog(null); }}>
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">{confirmDialog.title}</h3>
              <button className="modal-close" onClick={() => setConfirmDialog(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {confirmDialog.message}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDialog(null)}>취소</button>
              <button
                className={`btn ${confirmDialog.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={confirmDialog.onConfirm}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagePage;
